/*
 * Theory Digital — native collection browsing (load more / infinite scroll).
 *
 * Replaces the Scroll Bee app embed. Progressive enhancement over the stock
 * Trade numbered pagination rendered by snippets/td-collection-browse.liquid:
 * until this file runs, td-collection-browse is not :defined and the numbered
 * pagination stays visible, so JS-off buyers and crawlers keep working links.
 *
 * Pages are fetched through the Section Rendering API (?section_id=…&page=N),
 * same-origin with cookies, so price gating in snippets/price.liquid resolves
 * against the real customer session exactly as it does on a full page load.
 */

if (!customElements.get('td-collection-browse')) {
  class TDCollectionBrowse extends HTMLElement {
    // How close to the viewport the element gets before infinite mode fetches
    // the next page. Expressed as a percentage of viewport height so tall
    // desktop screens get proportionally more runway than phones — the point is
    // to have the next page appended before the buyer can scroll far enough to
    // see the spinner at all.
    static ROOT_MARGIN = '150% 0px';

    connectedCallback() {
      this.mode = this.dataset.mode;
      this.sectionId = this.dataset.sectionId;
      this.gridSelector = this.dataset.gridSelector || '#product-grid';

      this.button = this.querySelector('[data-td-load-more]');
      this.spinner = this.querySelector('.td-browse__spinner');
      this.count = this.querySelector('[data-td-count]');

      this.loading = false;
      this.onClick = this.onClick.bind(this);

      if (this.button) this.button.addEventListener('click', this.onClick);

      if (this.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.classList.add('td-browse--auto');
        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          rootMargin: TDCollectionBrowse.ROOT_MARGIN,
        });
        this.startObserving();
      }
    }

    disconnectedCallback() {
      // facets.js replaces #ProductGridContainer wholesale on filter/sort, which
      // tears this element down. Drop the observer so it can't fire against a
      // detached node.
      if (this.observer) this.observer.disconnect();
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    get nextUrl() {
      return this.dataset.nextUrl || '';
    }

    startObserving() {
      if (!this.observer || !this.nextUrl) return;
      // Re-observing forces a fresh callback, so a sentinel that is still in view
      // after an append keeps loading instead of stalling on no intersection change.
      this.observer.unobserve(this);
      this.observer.observe(this);
    }

    onIntersect(entries) {
      if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
    }

    onClick(event) {
      // A previous fetch failed — let the anchor navigate to ?page=N normally.
      if (this.dataset.tdFallback === 'true') return;
      event.preventDefault();
      this.loadNext({ focusNewItems: true });
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('td-browse--loading', isLoading);
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      if (this.button) {
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.button.classList.toggle('disabled', isLoading);
      }
    }

    sectionUrl(url) {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('section_id', this.sectionId);
      return parsed.toString();
    }

    fetchPage(url) {
      return fetch(this.sectionUrl(url))
        .then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        })
        .then((text) => new DOMParser().parseFromString(text, 'text/html'));
    }

    /*
     * Pull the page after next into memory as soon as one lands, so from the
     * second load onward there is nothing to wait for and the spinner never
     * paints. Only ever fires after a real load, so a visitor who lands on the
     * collection and doesn't scroll costs no extra request.
     */
    prefetchNext() {
      const url = this.nextUrl;
      if (!url || this.prefetch?.url === url) return;

      const entry = { url, doc: null };
      // Deliberately never rejects: a failed prefetch just leaves doc null and
      // loadNext falls through to a live fetch, which surfaces the error properly.
      entry.promise = this.fetchPage(url).then(
        (doc) => (entry.doc = doc),
        () => null
      );
      this.prefetch = entry;
    }

    async loadNext({ focusNewItems = false } = {}) {
      if (this.loading || !this.nextUrl) return;

      const grid = document.querySelector(this.gridSelector);
      if (!grid) return;

      const url = this.nextUrl;
      const cached = this.prefetch?.url === url ? this.prefetch : null;

      // Only paint the loading state when there is genuinely something to wait
      // for. A prefetch that has already landed appends without it.
      this.loading = true;
      if (!cached?.doc) this.setLoading(true);

      try {
        const doc = (cached ? await cached.promise : null) || (await this.fetchPage(url));
        const incomingGrid = doc.querySelector(this.gridSelector);
        if (!incomingGrid) throw new Error(`No ${this.gridSelector} in section response`);

        // Lets a host prepare for the new items before they connect. The bulk order list
        // uses it to adopt the volume-pricing ladders its rows read on construction.
        this.dispatchEvent(
          new CustomEvent('td-collection-browse:before-append', { bubbles: true, detail: { doc, url } })
        );

        const firstNewItem = incomingGrid.firstElementChild;
        const fragment = document.createDocumentFragment();
        while (incomingGrid.firstElementChild) fragment.appendChild(incomingGrid.firstElementChild);
        grid.appendChild(fragment);

        this.syncFrom(doc.querySelector('td-collection-browse'));

        this.dispatchEvent(new CustomEvent('td-collection-browse:appended', { bubbles: true, detail: { doc, url } }));

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(grid);

        if (focusNewItems && firstNewItem) this.focusItem(firstNewItem);
      } catch (error) {
        console.error('[td-collection-browse]', error);
        this.fallBackToLinks();
      } finally {
        this.prefetch = null;
        this.setLoading(false);
        // nextUrl has been advanced by syncFrom at this point, so this queues
        // the page after the one just appended.
        if (this.mode === 'infinite' && this.dataset.tdFallback !== 'true') this.prefetchNext();
      }
    }

    syncFrom(incoming) {
      this.dataset.nextUrl = incoming?.dataset.nextUrl || '';
      this.dataset.itemsShown = incoming?.dataset.itemsShown || this.dataset.itemsShown;

      const incomingCount = incoming?.querySelector('[data-td-count]');
      if (this.count && incomingCount) this.count.textContent = incomingCount.textContent;

      if (!this.nextUrl) {
        // Everything is on the page now.
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.remove();
        this.button = null;
        return;
      }

      if (this.button) this.button.href = this.nextUrl;
      this.startObserving();
    }

    focusItem(item) {
      const target = item.querySelector('a, button') || item;
      if (!target.hasAttribute('tabindex') && !target.matches('a, button')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    fallBackToLinks() {
      // Stop auto-loading and hand control back to the real pagination links so a
      // flaky network can't leave the buyer stranded mid-collection.
      this.dataset.tdFallback = 'true';
      this.classList.remove('td-browse--auto');
      this.classList.add('td-browse--fallback');
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('td-collection-browse', TDCollectionBrowse);
}
